import { Suspense, lazy } from "react";

import { Paths, Permissions } from "configs";
import { RouteProps } from "react-router-dom";

const HomePage = lazy(() => import("pages/Home"));

const EmployeeListPage = lazy(() => import("pages/Employees"));
const EmployeeDetailPage = lazy(() => import("pages/Employees/CRU/detail"));
const EmployeeCreatePage = lazy(() => import("pages/Employees/CRU/create"));

const GroupEmployeeListPage = lazy(() => import("pages/GroupEmployee"));
const GroupEmployeeDetailPage = lazy(
  () => import("pages/GroupEmployee/CRU/detail")
);
const GroupEmployeeCreatePage = lazy(
  () => import("pages/GroupEmployee/CRU/create")
);

const RoleListPage = lazy(() => import("pages/Roles"));
const RoleDetailPage = lazy(() => import("pages/Roles/CRU/detail"));
const RoleCreatePage = lazy(() => import("pages/Roles/CRU/create"));

const TitleListPage = lazy(() => import("pages/Titles"));
const TitleDetailPage = lazy(() => import("pages/Titles/CRU/detail"));
const TitleCreatePage = lazy(() => import("pages/Titles/CRU/create"));

const DepartmentListPage = lazy(() => import("pages/Departments"));
const DepartmentDetailPage = lazy(() => import("pages/Departments/CRU/detail"));
const DepartmentCreatePage = lazy(() => import("pages/Departments/CRU/create"));

const BranchesListPage = lazy(() => import("pages/Branches"));
const BranchesDetailPage = lazy(() => import("pages/Branches/CRU/detail"));
const BranchesCreatePage = lazy(() => import("pages/Branches/CRU/create"));

const FeaturesListPage = lazy(() => import("pages/Features"));
const FeaturesDetailPage = lazy(() => import("pages/Features/CRU/detail"));
const FeaturesCreatePage = lazy(() => import("pages/Features/CRU/create"));

const TenantsListPage = lazy(() => import("pages/Tenants"));
const TenantsDetailPage = lazy(() => import("pages/Tenants/CRU/detail"));
const TenantsCreatePage = lazy(() => import("pages/Tenants/CRU/create"));

const OrganizationsListPage = lazy(() => import("pages/Organizations"));
const OrganizationsDetailPage = lazy(
  () => import("pages/Organizations/CRU/detail")
);
const OrganizationsCreatePage = lazy(
  () => import("pages/Organizations/CRU/create")
);

const OrganizationalChartPage = lazy(() => import("pages/OrganizationalChart"));

const ApplicationPage = lazy(() => import("pages/Application"));
const ApplicationCreatePage = lazy(
  () => import("pages/Application/CRU/create")
);
const ApplicationDetailPage = lazy(
  () => import("pages/Application/CRU/detail")
);

const PartnerListPage = lazy(() => import("pages/Partners"));
const PartnerCreatePage = lazy(() => import("pages/Partners/CRU/create"));
const PartnerDetailPage = lazy(() => import("pages/Partners/CRU/detail"));

const WorkLevelListPage = lazy(() => import("pages/WorkLevel"));
const WorkLevelDetailPage = lazy(() => import("pages/WorkLevel/CRU/detail"));
const WorkLevelCreatePage = lazy(() => import("pages/WorkLevel/CRU/create"));

const ContractTypeListPage = lazy(() => import("pages/ContractType"));
const ContractTypeDetailPage = lazy(
  () => import("pages/ContractType/CRU/detail")
);
const ContractTypeCreatePage = lazy(
  () => import("pages/ContractType/CRU/create")
);

export const DashboardSuspenseWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <Suspense fallback={<div />}>{children}</Suspense>;

export const DashboardRoutes: Array<
  RouteProps & {
    useSuspense?: boolean;
    permission?: string | null;
  }
> = [
  {
    path: Paths.App,
    index: true,
    useSuspense: true,
    element: <OrganizationalChartPage />,
    permission: Permissions.OrgChart.View,
  },
  {
    path: Paths.Home,
    useSuspense: true,
    element: <HomePage />,
    permission: Permissions.Employee.ViewList,
  },

  /** EMPLOYEE */
  {
    path: Paths.Employees,
    useSuspense: true,
    element: <EmployeeListPage />,
    permission: Permissions.Employee.ViewList,
  },
  {
    path: Paths.EmployeesCreate,
    useSuspense: true,
    element: <EmployeeCreatePage />,
    permission: Permissions.Employee.Create,
  },
  {
    path: `${Paths.EmployeesDetail}/:id`,
    useSuspense: true,
    element: <EmployeeDetailPage />,
    permission: Permissions.Employee.ViewDetail,
  },
  /** END EMPLOYEE */

  /** TITLE */
  {
    path: Paths.Titles,
    useSuspense: true,
    element: <TitleListPage />,
    permission: Permissions.Title.ViewList,
  },
  {
    path: Paths.TitlesCreate,
    useSuspense: true,
    element: <TitleCreatePage />,
    permission: Permissions.Title.Create,
  },
  {
    path: `${Paths.TitlesDetail}/:id`,
    useSuspense: true,
    element: <TitleDetailPage />,
    permission: Permissions.Title.ViewDetail,
  },
  /** END TITLE */

  /** DEPARTMENT */
  {
    path: Paths.Departments,
    useSuspense: true,
    element: <DepartmentListPage />,
    permission: Permissions.Department.ViewList,
  },
  {
    path: Paths.DepartmentsCreate,
    useSuspense: true,
    element: <DepartmentCreatePage />,
    permission: Permissions.Department.Create,
  },
  {
    path: `${Paths.DepartmentsDetail}/:id`,
    useSuspense: true,
    element: <DepartmentDetailPage />,
    permission: Permissions.Department.ViewDetail,
  },
  /** END DEPARTMENT */

  /** BRANCH */
  {
    path: Paths.Branches,
    useSuspense: true,
    element: <BranchesListPage />,
    permission: Permissions.Branch.ViewList,
  },
  {
    path: Paths.BranchesCreate,
    useSuspense: true,
    element: <BranchesCreatePage />,
    permission: Permissions.Branch.Create,
  },
  {
    path: `${Paths.BranchesDetail}/:id`,
    useSuspense: true,
    element: <BranchesDetailPage />,
    permission: Permissions.Branch.ViewDetail,
  },
  /** END BRANCH */

  {
    path: Paths.OrganizationalChart,
    useSuspense: true,
    element: <OrganizationalChartPage />,
    permission: Permissions.OrgChart.View,
  },

  /** APPLICATION */
  {
    path: Paths.Applications,
    useSuspense: true,
    element: <ApplicationPage />,
    permission: Permissions.Application.ViewList,
  },
  {
    path: Paths.ApplicationCreate,
    useSuspense: true,
    element: <ApplicationCreatePage />,
    permission: Permissions.Application.Create,
  },
  {
    path: `${Paths.ApplicationDetail}/:id`,
    useSuspense: true,
    element: <ApplicationDetailPage />,
    permission: Permissions.Application.ViewDetail,
  },

  /** END APPLICATION */

  /** PARTNER */
  {
    path: Paths.Partners,
    useSuspense: true,
    element: <PartnerListPage />,
    permission: Permissions.Partner.ViewList,
  },
  {
    path: Paths.PartnersCreate,
    useSuspense: true,
    element: <PartnerCreatePage />,
    permission: Permissions.Partner.Create,
  },
  {
    path: `${Paths.PartnersDetail}/:id`,
    useSuspense: true,
    element: <PartnerDetailPage />,
    permission: Permissions.Partner.ViewDetail,
  },
  /** END PARTNER */

  /** ROLE */
  {
    path: Paths.Roles,
    useSuspense: true,
    element: <RoleListPage />,
    permission: Permissions.Role.ViewList,
  },
  {
    path: Paths.RolesCreate,
    useSuspense: true,
    element: <RoleCreatePage />,
    permission: Permissions.Role.Create,
  },
  {
    path: `${Paths.RoleDetail}/:id`,
    useSuspense: true,
    element: <RoleDetailPage />,
    permission: Permissions.Role.ViewDetail,
  },
  /** END ROLE */

  /** GROUP EMPLOYEE */
  {
    path: Paths.GroupEmployee,
    useSuspense: true,
    element: <GroupEmployeeListPage />,
    permission: Permissions.GroupEmployee.ViewList,
  },
  {
    path: Paths.GroupEmployeeCreate,
    useSuspense: true,
    element: <GroupEmployeeCreatePage />,
    permission: Permissions.GroupEmployee.Create,
  },
  {
    path: `${Paths.GroupEmployeeDetail}/:id`,
    useSuspense: true,
    element: <GroupEmployeeDetailPage />,
    permission: Permissions.GroupEmployee.ViewDetail,
  },
  /** END GROUP EMPLOYEE */

  /** FEATURE */
  {
    path: Paths.Features,
    useSuspense: true,
    element: <FeaturesListPage />,
    permission: Permissions.Feature.ViewList,
  },
  {
    path: Paths.FeatureCreate,
    useSuspense: true,
    element: <FeaturesCreatePage />,
    permission: Permissions.Feature.Create,
  },
  {
    path: `${Paths.FeatureDetail}/:id`,
    useSuspense: true,
    element: <FeaturesDetailPage />,
    permission: Permissions.Feature.ViewDetail,
  },
  /** END FEATURE */

  /** TENANT */
  {
    path: Paths.Tenants,
    useSuspense: true,
    element: <TenantsListPage />,
    permission: Permissions.Tenant.ViewList,
  },
  {
    path: Paths.TenantCreate,
    useSuspense: true,
    element: <TenantsCreatePage />,
    permission: Permissions.Tenant.Create,
  },
  {
    path: `${Paths.TenantDetail}/:id`,
    useSuspense: true,
    element: <TenantsDetailPage />,
    permission: Permissions.Tenant.ViewDetail,
  },
  /** END TENANT */

  /** ORGANIZATION */
  {
    path: Paths.Organizations,
    useSuspense: true,
    element: <OrganizationsListPage />,
    permission: Permissions.Organization.ViewList,
  },
  {
    path: Paths.OrganizationCreate,
    useSuspense: true,
    element: <OrganizationsCreatePage />,
    permission: Permissions.Organization.Create,
  },
  {
    path: `${Paths.OrganizationDetail}/:id`,
    useSuspense: true,
    element: <OrganizationsDetailPage />,
    permission: Permissions.Organization.ViewDetail,
  },
  /** END ORGANIZATION */

  /** WORK LEVEL */
  {
    path: Paths.WorkLevels,
    useSuspense: true,
    element: <WorkLevelListPage />,
    permission: Permissions.WorkLevel.ViewList,
  },
  {
    path: Paths.WorkLevelCreate,
    useSuspense: true,
    element: <WorkLevelCreatePage />,
    permission: Permissions.WorkLevel.Create,
  },
  {
    path: `${Paths.WorkLevelDetail}/:id`,
    useSuspense: true,
    element: <WorkLevelDetailPage />,
    permission: Permissions.WorkLevel.ViewDetail,
  },
  /** END WORK LEVEL */

  /** CONTRACT TYPE */
  {
    path: Paths.ContractTypes,
    useSuspense: true,
    element: <ContractTypeListPage />,
    permission: Permissions.WorkLevel.ViewList,
  },
  {
    path: Paths.ContractTypeCreate,
    useSuspense: true,
    element: <ContractTypeCreatePage />,
    permission: Permissions.WorkLevel.Create,
  },
  {
    path: `${Paths.ContractTypeDetail}/:id`,
    useSuspense: true,
    element: <ContractTypeDetailPage />,
    permission: Permissions.WorkLevel.ViewDetail,
  },
  /** END CONTRACT TYPE */
];
